import { SettingsHeadline } from "../containers/dynamicSettings/dynamicSettings";
import getEnvParam, {EnvParams, isProd, getProdParam} from "../../environment";
import { getGamSiteIds } from "../stores/sites/sitesModel";


const controls = {
    submit: {
        label: 'Save'
    }
}

const rowStructure = [
    {
        prop: "adsBundleVersion",
        label: {
            desc: 'Ads Bundle Version ',
            size: 'auto'
        },
        value: "",
        size: 12
    }
]
const bucket = getEnvParam(EnvParams.CDN_DFP_URL);

async function getTextFromUrlIfValid(url) {
    try {
        const response = await fetch(url);
        let txt
        if(typeof response.text === 'function') {
            txt = await response.text()
        }
        return response.ok && txt;
  
    } catch(error) {
        console.warn(error);
        return false;
    }
}

const resolveVersion = propArr => Array.isArray(propArr) && propArr[0] && propArr[0]['adsBundleVersion']

// parseInt (not Number) so a letter-suffixed segment (e.g. "37b" in "4.6.38b") still parses as
// its leading numeric value instead of NaN - Number('37b') is NaN, which `|| 0` then silently
// turns into 0, making the segment look OLDER than a plain "37" and misclassifying the version.
const isVersionAtLeast = (current, target) => {
  const cur = current.split('.').map(v => parseInt(v, 10));
  const tar = target.split('.').map(v => parseInt(v, 10));
  for (let i = 0; i < Math.max(cur.length, tar.length); i++) {
    const c = cur[i] || 0;
    const t = tar[i] || 0;
    if (c > t) return true;
    if (c < t) return false;
  }
  return true;
}
// Keep these in sync with server/lib/bundleVersions.js and MIGRATION-SE-ADS-MICROSERVICE.md
const PLATFORM_SPECIFIC_VERSION = '4.3.0';
const NORDIC_RENAME_VERSION = '4.6.38';

const validateBundleRequest = async (bucket, bundleVersion, pathType) => {
    const cdnUrl = `${bucket}/${bundleVersion}${pathType}/aller-ads.bundle.min.js`;
    const urlResponseText = await getTextFromUrlIfValid(cdnUrl);
    return urlResponseText && urlResponseText?.includes(bundleVersion);
}

// A two-part, non-global gamSiteId also gets a "sp-" (site-platform) config/bundle variant saved
// (see isSiteDomain in server/routes/api/fallback/create.js), which lives in its own CDN sub-folder.
const isSiteDomain = gamSiteId => !!gamSiteId && gamSiteId.split('.').length === 2 && !gamSiteId.includes('global');

// From NORDIC_RENAME_VERSION, the "default" site-platform sub-folder is retired in favor of a
// market-flavored "nordic-<countryCode>" bundle - there is no generic fallback bundle anymore.
const sitePlatformFolder = (bundleVersion, countryCode) =>
    isVersionAtLeast(bundleVersion, NORDIC_RENAME_VERSION) ? `nordic-${(countryCode || '').toLowerCase()}` : 'default';

// Markets whose "nordic-<market>" bundle is wired up in se-aller-ads source but not yet in its
// build list / on CDN (see MIGRATION-SE-ADS-MICROSERVICE.md). Don't hard-block saving a config for
// these - warn instead, so nordic-se/nordic-fi onboarding isn't blocked on their ~6-month-out ship date.
const PROVISIONAL_NORDIC_MARKETS = ['dk', 'no'];

// Every CDN path this bundleVersion will actually be served from for this site. `soft: true` means
// a missing bundle should warn rather than block the save (see PROVISIONAL_NORDIC_MARKETS above).
const requiredPathChecks = (bundleVersion, countryCode, gamSiteId) => {
    if (!isVersionAtLeast(bundleVersion, PLATFORM_SPECIFIC_VERSION)) return [{ pathType: '', soft: false }];

    const checks = [{ pathType: `/${countryCode}`, soft: false }];
    if (isSiteDomain(gamSiteId)) {
        const market = (countryCode || '').toLowerCase();
        const folder = sitePlatformFolder(bundleVersion, countryCode);
        const soft = isVersionAtLeast(bundleVersion, NORDIC_RENAME_VERSION) && PROVISIONAL_NORDIC_MARKETS.includes(market);
        checks.push({ pathType: `/${folder}`, soft, market, folder });
    }
    return checks;
}

// Returns { isValid, warning } - isValid gates whether the save is allowed to proceed, warning (if
// set) is a message to surface to the user even though the save was allowed.
const validCdnBundleExists = async (props) => {
    const { propArr, countryCode, gamSiteId } = props;

    const bundleVersion = resolveVersion(propArr)

    if (bundleVersion === '') return { isValid: true };
    if (!(bundleVersion && bucket !== '')) return { isValid: false };

    let warning = null;
    for (const { pathType, soft, market, folder } of requiredPathChecks(bundleVersion, countryCode, gamSiteId)) {
        let isValid = await validateBundleRequest(bucket, bundleVersion, pathType);
        if (!isValid && !isProd()) {
            isValid = await validateBundleRequest(getProdParam().cdnDfpUrl, bundleVersion, pathType);
        }
        if (!isValid) {
            if (soft) {
                warning = `Bundle ${bundleVersion} saved, but the "${folder}" bundle for market "${market}" is not yet on CDN - ads will 404 for this site-platform variant until it ships.`;
                continue;
            }
            return { isValid: false };
        }
    }
    return { isValid: true, warning };
}

const saveConfigOnCdn = async (gamSiteId, sites = []) => {

    const gamSiteIds = getGamSiteIds({ gamSiteId, isGlobal: `${gamSiteId.includes('global')}`}, sites);
    
    try {
        const baseURL = getEnvParam(EnvParams.BASE_URL);

        const response = await fetch(`${baseURL}/fallback`, {
            headers: { "Content-Type": "application/json" },
            method: "POST",
            body: JSON.stringify({ gamSiteIds }),
        });
        if (response.status == 200) {
            console.log(`ACC config were saved on CDN for ${gamSiteId} and children`);
        } else { 
            throw new Error(`Failed to save ACC config on CDN for ${gamSiteId} and children`, response.status);
        }
        return response.ok;
    } catch(error) {
        console.warn(error);
        return false;
    }
}

const settings = {
    headlineType: SettingsHeadline.LEADING,
    endpoint: 'adsBundleVersions',
    inputValue : 'adsBundleVersion',
    description: `Here below you enter unique Ads Bundle Versions, that exists on CDN ${bucket}`,
    toastMessage: {
        success: props => `Bundle ${resolveVersion(props) || ''} has been saved`,
        error: props => `Bundle ${resolveVersion(props) || ''} could Not be verified on CDN ${isProd() ? '' : 'Stage Nor'} Prod`
    },
    inputs: 'single',
    prepRequest: (rows, additional) => rows.map(row => ({ ...row, ...additional })),
    validateFunc: validCdnBundleExists,
    onSaveComplete: saveConfigOnCdn
}

export const ADS_BUNDLE_VERSION = {
    controls,
    rowStructure,
    settings
}